import { useCallback, useMemo, useState } from 'react';
import type { ObjectTableProps } from 'components/ObjectTable/ObjectTable';
import type { IObjectTableFilter } from 'components/ObjectTable/ObjectTableFilter';
import {
  SelectObjects,
  useSelection,
} from 'components/SelectObjectsModal/SelectObjectsModal';
import { getText } from 'i18n/intl';
import { type TFindObjectsPayload, findMoreObjects } from 'slices/object.slice';
import type { TType } from '@timeedit/types';
import type { TTEObject } from '@timeedit/types/lib/types';
import {
  Form,
  type FormInstance,
  Typography,
} from '@timeedit/ui-components/antd';
import { type BulkEditAction, BulkEditMatchRule } from './common';

type Props = {
  action: BulkEditAction;
  form: FormInstance;
  objectTypes: TType[];
  relationType:
    | 'members'
    | 'relations'
    | 'availabilityRelations'
    | 'optionalRelations';
  onChangeOverride?: (value: any[], fieldKey: string) => any;
};

const useOnChange = (
  form: FormInstance,
  fieldKey: string,
  onChangeOverride?: (value: any[], fieldKey: string) => any
) => {
  // Keep track of selected IDs so we can change object type in filter without losing the selection
  const [alreadySelectedIds, setAlreadySelectedIds] = useState<number[]>([]);

  const onChange = useCallback(
    (selectedRowKeys, selectedRows) => {
      setAlreadySelectedIds(selectedRowKeys);

      if (onChangeOverride) {
        onChangeOverride(selectedRows, fieldKey);
        return;
      }

      form.setFieldValue(
        fieldKey,
        selectedRows.map((item) => item.id)
      );
    },
    [form, fieldKey, onChangeOverride]
  );

  return { onChange, alreadySelectedIds: alreadySelectedIds };
};

const BulkEditAssociation = ({
  action,
  form,
  relationType,
  objectTypes,
  onChangeOverride,
}: Props) => {
  const showAdd = action === 'add';
  const showFindAndReplace = action === 'findReplace';
  const showReplaceAll = action === 'replaceAll';
  const showRemoveSpecific = action === 'removeSpecific';

  return (
    <div>
      {showAdd && (
        <Add
          objectTypes={objectTypes}
          form={form}
          relationType={relationType}
          onChangeOverride={onChangeOverride}
        />
      )}
      {showFindAndReplace && (
        <FindAndReplace
          objectTypes={objectTypes}
          form={form}
          relationType={relationType}
          onChangeOverride={onChangeOverride}
        />
      )}
      {showReplaceAll && (
        <ReplaceAll
          objectTypes={objectTypes}
          form={form}
          relationType={relationType}
          onChangeOverride={onChangeOverride}
        />
      )}
      {showRemoveSpecific && (
        <RemoveSpecific
          objectTypes={objectTypes}
          form={form}
          relationType={relationType}
          onChangeOverride={onChangeOverride}
        />
      )}
    </div>
  );
};

type SubComponentProps = {
  objectTypes: TType[];
  form: FormInstance;
  relationType:
    | 'members'
    | 'relations'
    | 'availabilityRelations'
    | 'optionalRelations';
  onChangeOverride?: (value: any[], fieldKey: string) => any;
};

const useSelectObjectsProps = () => {
  const [objects, setObjects] = useState<TTEObject[]>([]);
  const [filter, setFilter] = useState<IObjectTableFilter | undefined>();
  const resetFn = useCallback(() => {
    setObjects([]);
  }, []);

  const fetchObjects: ObjectTableProps['fetchFn'] = useCallback(
    async (payload: TFindObjectsPayload) => {
      const response = await findMoreObjects(payload);

      setObjects((previousObjects) => {
        const newObjects = response?.results ?? [];
        return [...previousObjects, ...newObjects];
      });

      return response;
    },
    []
  );

  return useMemo(
    () => ({
      objects,
      filter,
      setFilter,
      resetFn,
      fetchObjects,
    }),
    [objects, filter, setFilter, resetFn, fetchObjects]
  );
};

const Add = ({
  objectTypes,
  form,
  relationType,
  onChangeOverride,
}: SubComponentProps) => {
  const { objects, filter, setFilter, resetFn, fetchObjects } =
    useSelectObjectsProps();
  const fieldName = `${relationType}_add` as const;

  const { onChange, alreadySelectedIds } = useOnChange(
    form,
    fieldName,
    onChangeOverride
  );

  const { rowSelection } = useSelection(objects, alreadySelectedIds, onChange);

  return (
    <Form.Item
      name={fieldName}
      noStyle
      rules={[{ required: true, message: 'New values required' }]}
    >
      <SelectObjects
        availableObjectTypes={objectTypes}
        rowSelection={rowSelection}
        filter={filter}
        setFilter={setFilter}
        resetFn={resetFn}
        objects={objects}
        fetchFn={fetchObjects}
      />
    </Form.Item>
  );
};

const FindAndReplace = ({
  objectTypes,
  form,
  relationType,
  onChangeOverride,
}: SubComponentProps) => {
  const {
    objects: replaceObjects,
    filter: replaceFilter,
    setFilter: setReplaceFilter,
    resetFn: resetReplaceFn,
    fetchObjects: fetchReplaceObjects,
  } = useSelectObjectsProps();

  const {
    objects: withObjects,
    resetFn: resetWithFn,
    fetchObjects: fetchWithObjects,
    filter: withFilter,
    setFilter: setWithFilter,
  } = useSelectObjectsProps();

  const fieldNameReplace = `${relationType}_replace` as const;
  const fieldNameWith = `${relationType}_with` as const;

  const {
    onChange: onChangeReplace,
    alreadySelectedIds: alreadySelectedReplaceIds,
  } = useOnChange(form, fieldNameReplace, onChangeOverride);
  const { onChange: onChangeWith, alreadySelectedIds: alreadySelectedWithIds } =
    useOnChange(form, fieldNameWith, onChangeOverride);

  const { rowSelection: rowSelectionReplace } = useSelection(
    replaceObjects,
    alreadySelectedReplaceIds,
    onChangeReplace
  );

  const { rowSelection: rowSelectionWith } = useSelection(
    withObjects,
    alreadySelectedWithIds,
    onChangeWith
  );

  return (
    <div style={{ display: 'flex', gap: '24px', flexDirection: 'column' }}>
      <Form.Item
        name={fieldNameReplace}
        noStyle
        rules={[{ required: true, message: 'Value to find required' }]}
      >
        <Typography.Title level={5}>
          {getText('object_manager.bulk-edit-modal.placeholder.search')}
        </Typography.Title>
        <BulkEditMatchRule fieldId={relationType} isMultiple />
        <SelectObjects
          availableObjectTypes={objectTypes}
          rowSelection={rowSelectionReplace}
          filter={replaceFilter}
          setFilter={setReplaceFilter}
          resetFn={resetReplaceFn}
          objects={replaceObjects}
          fetchFn={fetchReplaceObjects}
        />
      </Form.Item>

      <Form.Item
        name={fieldNameWith}
        noStyle
        rules={[{ required: true, message: 'Replacement value(s) required' }]}
      >
        <Typography.Title level={5}>
          {getText(
            'object_manager.bulk-edit-modal.findReplace.replace-input-label'
          )}
        </Typography.Title>
        <SelectObjects
          availableObjectTypes={objectTypes}
          rowSelection={rowSelectionWith}
          filter={withFilter}
          setFilter={setWithFilter}
          resetFn={resetWithFn}
          objects={withObjects}
          fetchFn={fetchWithObjects}
        />
      </Form.Item>
    </div>
  );
};

const ReplaceAll = ({
  objectTypes,
  form,
  relationType,
  onChangeOverride,
}: SubComponentProps) => {
  const { objects, filter, setFilter, resetFn, fetchObjects } =
    useSelectObjectsProps();

  const fieldName = `${relationType}_replaceAll` as const;

  const { onChange, alreadySelectedIds } = useOnChange(
    form,
    fieldName,
    onChangeOverride
  );
  const { rowSelection } = useSelection(objects, alreadySelectedIds, onChange);
  return (
    <Form.Item
      name={fieldName}
      noStyle
      rules={[{ required: true, message: 'New value(s) required' }]}
    >
      <SelectObjects
        availableObjectTypes={objectTypes}
        rowSelection={rowSelection}
        filter={filter}
        setFilter={setFilter}
        resetFn={resetFn}
        objects={objects}
        fetchFn={fetchObjects}
      />
    </Form.Item>
  );
};

const RemoveSpecific = ({
  objectTypes,
  form,
  relationType,
  onChangeOverride,
}: SubComponentProps) => {
  const { objects, filter, setFilter, resetFn, fetchObjects } =
    useSelectObjectsProps();

  const fieldName = `${relationType}_removeSpecific` as const;

  const { onChange, alreadySelectedIds } = useOnChange(
    form,
    fieldName,
    onChangeOverride
  );
  const { rowSelection } = useSelection(objects, alreadySelectedIds, onChange);
  return (
    <Form.Item
      name={fieldName}
      noStyle
      rules={[{ required: true, message: 'Value(s) to remove required' }]}
    >
      <SelectObjects
        availableObjectTypes={objectTypes}
        rowSelection={rowSelection}
        filter={filter}
        setFilter={setFilter}
        resetFn={resetFn}
        objects={objects}
        fetchFn={fetchObjects}
      />
    </Form.Item>
  );
};

export default BulkEditAssociation;
